import { useCallback, useRef, useState, useEffect } from 'react'

import { ChatCloseIcon, PlusIcon, DeleteIcon, BroadcastIcon, PlayIcon, PauseIcon } from '@/src/assets/icons'

import { MediaToggle } from '@/src/components/controls/MediaToggle'

import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import { selectBranding, selectIsAdmin, selectRoomName } from '@/src/store/slices/meetData'
import {
  selectIsOpenMedia,
  selectPresentations,
  selectVideos,
  selectAudios,
  selectActiveMedia,
  selectPlayingAudioId,
  addVideo,
  addAudio,
  removePresentation,
  removeVideo,
  removeAudio,
  setActiveMedia,
  setPlayingAudioId,
  setMediaLoading,
  selectMediaLoading,
  IVideo,
  IAudio,
} from '@/src/store/slices/media'
import { selectRoomState } from '@/src/store/slices/room'

export interface MediaPanelProps {
  hideCloseBtn?: boolean
}

export const MediaPanel = ({ hideCloseBtn }: MediaPanelProps) => {
  const dispatch = useAppDispatch()

  const isOpen = useAppSelector(selectIsOpenMedia)
  const presentations = useAppSelector(selectPresentations)
  const videos = useAppSelector(selectVideos)
  const audios = useAppSelector(selectAudios)
  const activeMedia = useAppSelector(selectActiveMedia)
  const playingAudioId = useAppSelector(selectPlayingAudioId)
  const isLoading = useAppSelector(selectMediaLoading)
  const isAdmin = useAppSelector(selectIsAdmin)
  const { text_primary_color, primary_color } = useAppSelector(selectBranding)
  const roomName = useAppSelector(selectRoomName)
  const { livekit_url } = useAppSelector(selectRoomState)

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    presentations: true,
    videos: true,
    audios: true,
  })

  const videoInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({})

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }, [])

  const handleSectionKeyDown = useCallback(
    (section: string, event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        toggleSection(section)
      }
    },
    [toggleSection]
  )

  const generateId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`

  const handleVideoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (!file.type.startsWith('video/')) {
        alert('Пожалуйста, выберите видео файл')
        return
      }

      dispatch(setMediaLoading(true))

      try {
        const formData = new FormData()
        formData.append('file', file)

        const query = new URLSearchParams()
        if (livekit_url) query.set('livekit_url', livekit_url)
        if (roomName) query.set('roomName', roomName)
        const response = await fetch(`/api/media/upload?${query.toString()}`, {
          method: 'POST',
          body: formData,
        })

        if (!response.ok) {
          throw new Error('Failed to upload video')
        }

        const data = await response.json()

        const video: IVideo = {
          id: generateId(),
          name: file.name,
          url: data.url,
          thumbnailUrl: data.thumbnailUrl,
          createdAt: Date.now(),
        }

        dispatch(addVideo(video))
      } catch (error) {
        console.error('Error uploading video:', error)
        alert('Ошибка при загрузке видео')
      } finally {
        dispatch(setMediaLoading(false))
        if (videoInputRef.current) {
          videoInputRef.current.value = ''
        }
      }
    },
    [dispatch, livekit_url, roomName]
  )

  const handleAudioUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      if (!file.type.startsWith('audio/')) {
        alert('Пожалуйста, выберите аудио файл')
        return
      }

      dispatch(setMediaLoading(true))

      try {
        const formData = new FormData()
        formData.append('file', file)

        const query = new URLSearchParams()
        if (livekit_url) query.set('livekit_url', livekit_url)
        if (roomName) query.set('roomName', roomName)
        const response = await fetch(`/api/media/upload?${query.toString()}`, {
          method: 'POST',
          body: formData,
        })

        if (!response.ok) {
          throw new Error('Failed to upload audio')
        }

        const data = await response.json()

        const audio: IAudio = {
          id: generateId(),
          name: file.name,
          url: data.url,
          createdAt: Date.now(),
        }

        dispatch(addAudio(audio))
      } catch (error) {
        console.error('Error uploading audio:', error)
        alert('Ошибка при загрузке аудио')
      } finally {
        dispatch(setMediaLoading(false))
        if (audioInputRef.current) {
          audioInputRef.current.value = ''
        }
      }
    },
    [dispatch, livekit_url, roomName]
  )

  const handleBroadcast = useCallback(
    (id: string, type: 'presentation' | 'video' | 'audio') => {
      if (activeMedia.id === id && activeMedia.type === type) {
        dispatch(setActiveMedia({ id: null, type: null }))
      } else {
        dispatch(setActiveMedia({ id, type }))
      }
    },
    [dispatch, activeMedia]
  )

  const handleDelete = useCallback(
    (id: string, type: 'presentation' | 'video' | 'audio') => {
      if (type === 'presentation') {
        dispatch(removePresentation(id))
      } else if (type === 'video') {
        dispatch(removeVideo(id))
      } else {
        dispatch(removeAudio(id))
      }
    },
    [dispatch]
  )

  const handleAudioPlayPause = useCallback(
    (audioId: string) => {
      const audioEl = audioRefs.current[audioId]
      if (!audioEl) return

      if (playingAudioId === audioId) {
        audioEl.pause()
        dispatch(setPlayingAudioId(null))
      } else {
        if (playingAudioId) {
          const prevAudio = audioRefs.current[playingAudioId]
          if (prevAudio) prevAudio.pause()
        }
        audioEl.play()
        dispatch(setPlayingAudioId(audioId))
      }
    },
    [dispatch, playingAudioId]
  )

  const handleAudioSeek = useCallback((audioId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const audioEl = audioRefs.current[audioId]
    if (!audioEl) return
    audioEl.currentTime = parseFloat(event.target.value)
  }, [])

  if (!isOpen) return null

  return (
    <aside className="lk-media-panel" style={{ color: text_primary_color }}>
      <header className="lk-media-panel-header">
        <span className="lk-media-panel-title" style={{ color: text_primary_color }}>
          Медиа
        </span>
        {!hideCloseBtn && (
          <MediaToggle className="lk-close-button" closeBar>
            <ChatCloseIcon />
          </MediaToggle>
        )}
      </header>

      {isLoading && (
        <div className="lk-media-loading">
          <span>Загрузка...</span>
        </div>
      )}

      <div className="lk-media-panel-content">
        <div className="lk-media-section">
          <div
            className="lk-media-section-header"
            onClick={() => toggleSection('presentations')}
            onKeyDown={(e) => handleSectionKeyDown('presentations', e)}
            role="button"
            tabIndex={0}
          >
            <span>Презентации ({presentations.length})</span>
            <svg
              className={`lk-media-chevron ${expandedSections.presentations ? 'expanded' : ''}`}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>

          {expandedSections.presentations && (
            <div className="lk-media-section-content">
              {isAdmin && (
                <button
                  className="lk-media-add-btn"
                  disabled
                  style={{ borderColor: primary_color }}
                >
                  <PlusIcon fill={text_primary_color} />
                  <span>Добавить презентацию</span>
                </button>
              )}

              <div className="lk-media-items">
                {presentations.map((presentation) => (
                  <div key={presentation.id} className="lk-media-item presentation-item">
                    <div className="lk-media-item-preview">
                      {presentation.slides[0] ? (
                        <img src={presentation.slides[0]} alt={presentation.name} />
                      ) : (
                        <div className="lk-media-placeholder">PDF</div>
                      )}
                    </div>
                    <div className="lk-media-item-info">
                      <span className="lk-media-item-name">{presentation.name}</span>
                      <span className="lk-media-item-slides">{presentation.slides.length} слайд(ов)</span>
                    </div>
                    <div className="lk-media-item-actions">
                      <button
                        className={`lk-media-action-btn broadcast ${
                          activeMedia.id === presentation.id && activeMedia.type === 'presentation'
                            ? 'active'
                            : ''
                        }`}
                        onClick={() => handleBroadcast(presentation.id, 'presentation')}
                        title="В эфир"
                      >
                        <BroadcastIcon />
                        <span>В эфир</span>
                      </button>
                      {isAdmin && (
                        <button
                          className="lk-media-action-btn delete"
                          onClick={() => handleDelete(presentation.id, 'presentation')}
                          title="Удалить"
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {presentations.length === 0 && (
                  <div className="lk-media-empty">Нет загруженных презентаций</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lk-media-section">
          <div
            className="lk-media-section-header"
            onClick={() => toggleSection('videos')}
            onKeyDown={(e) => handleSectionKeyDown('videos', e)}
            role="button"
            tabIndex={0}
          >
            <span>Видео ({videos.length})</span>
            <svg
              className={`lk-media-chevron ${expandedSections.videos ? 'expanded' : ''}`}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>

          {expandedSections.videos && (
            <div className="lk-media-section-content">
              {isAdmin && (
                <button
                  className="lk-media-add-btn"
                  onClick={() => videoInputRef.current?.click()}
                  style={{ borderColor: primary_color }}
                >
                  <PlusIcon fill={text_primary_color} />
                  <span>Добавить видео</span>
                </button>
              )}
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                style={{ display: 'none' }}
                onChange={handleVideoUpload}
              />

              <div className="lk-media-items">
                {videos.map((video) => (
                  <div key={video.id} className="lk-media-item video-item">
                    <div className="lk-media-item-preview video-preview">
                      <video
                        src={video.url}
                        preload="metadata"
                        controls
                        playsInline
                        poster={video.thumbnailUrl}
                      />
                    </div>
                    <div className="lk-media-item-info">
                      <span className="lk-media-item-name">{video.name}</span>
                    </div>
                    <div className="lk-media-item-actions">
                      <button
                        className={`lk-media-action-btn broadcast ${
                          activeMedia.id === video.id && activeMedia.type === 'video' ? 'active' : ''
                        }`}
                        onClick={() => handleBroadcast(video.id, 'video')}
                        title="В эфир"
                      >
                        <BroadcastIcon />
                        <span>В эфир</span>
                      </button>
                      {isAdmin && (
                        <button
                          className="lk-media-action-btn delete"
                          onClick={() => handleDelete(video.id, 'video')}
                          title="Удалить"
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {videos.length === 0 && <div className="lk-media-empty">Нет загруженных видео</div>}
              </div>
            </div>
          )}
        </div>

        <div className="lk-media-section">
          <div
            className="lk-media-section-header"
            onClick={() => toggleSection('audios')}
            onKeyDown={(e) => handleSectionKeyDown('audios', e)}
            role="button"
            tabIndex={0}
          >
            <span>Аудио ({audios.length})</span>
            <svg
              className={`lk-media-chevron ${expandedSections.audios ? 'expanded' : ''}`}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>

          {expandedSections.audios && (
            <div className="lk-media-section-content">
              {isAdmin && (
                <button
                  className="lk-media-add-btn"
                  onClick={() => audioInputRef.current?.click()}
                  style={{ borderColor: primary_color }}
                >
                  <PlusIcon fill={text_primary_color} />
                  <span>Добавить аудио</span>
                </button>
              )}
              <input
                ref={audioInputRef}
                type="file"
                accept="audio/*"
                style={{ display: 'none' }}
                onChange={handleAudioUpload}
              />

              <div className="lk-media-items">
                {audios.map((audio) => (
                  <AudioItem
                    key={audio.id}
                    audio={audio}
                    isPlaying={playingAudioId === audio.id}
                    isActive={activeMedia.id === audio.id && activeMedia.type === 'audio'}
                    isAdmin={isAdmin}
                    onPlayPause={() => handleAudioPlayPause(audio.id)}
                    onSeek={(e) => handleAudioSeek(audio.id, e)}
                    onBroadcast={() => handleBroadcast(audio.id, 'audio')}
                    onDelete={() => handleDelete(audio.id, 'audio')}
                    audioRef={(el) => {
                      audioRefs.current[audio.id] = el
                    }}
                  />
                ))}
                {audios.length === 0 && <div className="lk-media-empty">Нет загруженных аудио</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

interface AudioItemProps {
  audio: IAudio
  isPlaying: boolean
  isActive: boolean
  isAdmin: boolean
  onPlayPause: () => void
  onSeek: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBroadcast: () => void
  onDelete: () => void
  audioRef: (el: HTMLAudioElement | null) => void
}

const AudioItem = ({
  audio,
  isPlaying,
  isActive,
  isAdmin,
  onPlayPause,
  onSeek,
  onBroadcast,
  onDelete,
  audioRef,
}: AudioItemProps) => {
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const audioElRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const audioEl = audioElRef.current
    if (!audioEl) return

    const handleTimeUpdate = () => setCurrentTime(audioEl.currentTime)
    const handleLoadedMetadata = () => setDuration(audioEl.duration)
    const handleEnded = () => {
      setCurrentTime(0)
    }

    audioEl.addEventListener('timeupdate', handleTimeUpdate)
    audioEl.addEventListener('loadedmetadata', handleLoadedMetadata)
    audioEl.addEventListener('ended', handleEnded)

    return () => {
      audioEl.removeEventListener('timeupdate', handleTimeUpdate)
      audioEl.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audioEl.removeEventListener('ended', handleEnded)
    }
  }, [])

  const formatTime = (time: number) => {
    const mins = Math.floor(time / 60)
    const secs = Math.floor(time % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="lk-media-item audio-item">
      <audio
        ref={(el) => {
          audioElRef.current = el
          audioRef(el)
        }}
        src={audio.url}
        preload="metadata"
      />
      <div className="lk-audio-controls">
        <button className="lk-audio-play-btn" onClick={onPlayPause}>
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <div className="lk-audio-info">
          <span className="lk-audio-name">{audio.name}</span>
          <div className="lk-audio-progress">
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={onSeek}
              className="lk-audio-slider"
            />
            <span className="lk-audio-time">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
      <div className="lk-media-item-actions">
        <button
          className={`lk-media-action-btn broadcast ${isActive ? 'active' : ''}`}
          onClick={onBroadcast}
          title="В эфир"
        >
          <BroadcastIcon />
          <span>В эфир</span>
        </button>
        {isAdmin && (
          <button className="lk-media-action-btn delete" onClick={onDelete} title="Удалить">
            <DeleteIcon />
          </button>
        )}
      </div>
    </div>
  )
}
